import React, { useState, useMemo } from 'react';
import { Button, Card, Table, Space, Tag, DatePicker, Select, Statistic, Row, Col, Progress, Alert } from 'antd';
import { BarChartOutlined, WarningOutlined, CheckCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useCapacityPlan, useGenerateCapacity, useLoadOpenWork, useWorkstations } from '@/hooks/useManufacturing';
import type { CapacityPlan } from '@/services/manufacturing.service';

function hoursText(value: unknown): string {
  const hours = Number(value);
  return Number.isFinite(hours) ? hours.toFixed(1) : '0.0';
}

const CapacityPlanningPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>('all');
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const generateCapacity = useGenerateCapacity();
  const loadOpenWork = useLoadOpenWork();

  // API hooks
  const { data: workstations = [] } = useWorkstations();
  
  // Fetch capacity plans for all workstations or selected workstation
  const { data: capacityPlans = [], isLoading } = useCapacityPlan(
    selectedWorkstation === 'all' ? '' : selectedWorkstation,
    dateRange[0].format('YYYY-MM-DD'),
    dateRange[1].format('YYYY-MM-DD')
  );

  // Extend capacity plan with workstation name
  const enrichedCapacityPlans = useMemo(() => {
    return (capacityPlans as CapacityPlan[]).map(plan => {
      const workstation = workstations.find(w => w.id === (plan as any).workstation_id);
      return {
        ...plan,
        workstation_name: workstation?.workstation_name || 'Unknown',
        shift_name: (plan as any).shift?.shift_name || '-',
      };
    });
  }, [capacityPlans, workstations]);

  // Use enriched data for display
  const capacityPlansData = selectedWorkstation === 'all' 
    ? enrichedCapacityPlans 
    : enrichedCapacityPlans.filter((p: any) => p.workstation_id === selectedWorkstation);

  const columns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'plan_date',
      key: 'plan_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MMM-YYYY'),
      sorter: (a, b) => dayjs(a.plan_date).unix() - dayjs(b.plan_date).unix(),
    },
    {
      title: 'Day',
      dataIndex: 'day_of_week',
      key: 'day_of_week',
      width: 100,
    },
    {
      title: 'Workstation',
      dataIndex: 'workstation_name',
      key: 'workstation_name',
      width: 180,
    },
    {
      title: 'Shift',
      dataIndex: 'shift_name',
      key: 'shift_name',
      width: 150,
    },
    {
      title: 'Available (hrs)',
      dataIndex: 'available_capacity_hours',
      key: 'available_capacity_hours',
      width: 120,
      align: 'right',
      render: (hours: number) => hoursText(hours),
    },
    {
      title: 'Planned (hrs)',
      dataIndex: 'planned_capacity_hours',
      key: 'planned_capacity_hours',
      width: 120,
      align: 'right',
      render: (hours: number) => hoursText(hours),
    },
    {
      title: 'Actual (hrs)',
      dataIndex: 'actual_capacity_hours',
      key: 'actual_capacity_hours',
      width: 120,
      align: 'right',
      render: (hours: number) => hoursText(hours),
    },
    {
      title: 'Overtime (hrs)',
      dataIndex: 'overtime_hours',
      key: 'overtime_hours',
      width: 120,
      align: 'right',
      render: (hours: number) => (
        Number(hours) > 0 ? <Tag color="orange">{hoursText(hours)}</Tag> : '-'
      ),
    },
    {
      title: 'Idle (hrs)',
      dataIndex: 'idle_hours',
      key: 'idle_hours',
      width: 100,
      align: 'right',
      render: (hours: number) => hoursText(hours),
    },
    {
      title: 'Utilization',
      dataIndex: 'utilization_percentage',
      key: 'utilization_percentage',
      width: 150,
      align: 'center',
      render: (percentage: number) => {
        const value = Number(percentage) || 0;
        return (
        <Progress
          percent={value}
          size="small"
          status={value > 100 ? 'exception' : value > 80 ? 'success' : 'normal'}
          strokeColor={value > 100 ? '#f5222d' : value > 80 ? '#52c41a' : '#145A54'}
        />
        );
      },
      sorter: (a, b) => a.utilization_percentage - b.utilization_percentage,
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency_percentage',
      key: 'efficiency_percentage',
      width: 100,
      align: 'center',
      render: (percentage: number) => `${Number(percentage) || 0}%`,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: any, record: CapacityPlan) => {
        if (record.is_bottleneck) {
          return <Tag color="red" icon={<WarningOutlined />}>Bottleneck</Tag>;
        }
        if (record.is_overloaded) {
          return <Tag color="orange" icon={<ThunderboltOutlined />}>Overloaded</Tag>;
        }
        return <Tag color="green" icon={<CheckCircleOutlined />}>Normal</Tag>;
      },
    },
  ];

  const calculateSummary = () => {
    const totalAvailable = capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.available_capacity_hours || 0), 0);
    const totalPlanned = capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.planned_capacity_hours || 0), 0);
    const totalActual = capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.actual_capacity_hours || 0), 0);
    const totalOvertime = capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.overtime_hours || 0), 0);
    const totalIdle = capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.idle_hours || 0), 0);
    const avgUtilization = capacityPlansData.length > 0 
      ? capacityPlansData.reduce((sum: number, p: any) => sum + Number(p.utilization_percentage || 0), 0) / capacityPlansData.length 
      : 0;
    const bottleneckCount = capacityPlansData.filter((p: any) => p.is_bottleneck).length;
    const overloadedCount = capacityPlansData.filter((p: any) => p.is_overloaded).length;

    return {
      totalAvailable,
      totalPlanned,
      totalActual,
      totalOvertime,
      totalIdle,
      avgUtilization,
      bottleneckCount,
      overloadedCount,
    };
  };

  const summary = calculateSummary();

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>Capacity Planning</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              onClick={() =>
                generateCapacity.mutate({
                  workstation_id: selectedWorkstation,
                  start_date: dateRange[0].format('YYYY-MM-DD'),
                  end_date: dateRange[1].format('YYYY-MM-DD'),
                })
              }
              loading={generateCapacity.isPending}
            >
              Generate capacity
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                const result = await loadOpenWork.mutateAsync({
                  start_date: dateRange[0].format('YYYY-MM-DD'),
                  end_date: dateRange[1].format('YYYY-MM-DD'),
                });
                const parts = [
                  `${Number(result.planned_hours || 0).toFixed(1)} planned hours`,
                  `${Number(result.actual_hours || 0).toFixed(1)} actual hours from end-of-day close and work order operations`,
                ];
                if (result.overloaded_buckets) parts.push(`${result.overloaded_buckets} overloaded buckets`);
                if (result.unassigned_hours) parts.push(`${result.unassigned_hours.toFixed(1)} planned h have no workstation`);
                if (result.unbucketed_hours) parts.push(`${result.unbucketed_hours.toFixed(1)} planned h have no capacity bucket. Generate capacity first.`);
                if (result.actual_unassigned_hours) parts.push(`${Number(result.actual_unassigned_hours).toFixed(1)} actual h have no workstation`);
                if (result.actual_unbucketed_hours) parts.push(`${Number(result.actual_unbucketed_hours).toFixed(1)} actual h have no capacity bucket`);
                setLoadNote(parts.join('. '));
              }}
              loading={loadOpenWork.isPending}
            >
              Load open work
            </Button>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange([dates[0]!, dates[1]!])}
              format="DD-MMM-YYYY"
            />
            <Select
              value={selectedWorkstation}
              onChange={setSelectedWorkstation}
              style={{ width: 250 }}
            >
              <Select.Option value="all">All Workstations</Select.Option>
              {workstations.map(ws => (
                <Select.Option key={ws.id} value={ws.id!}>{ws.workstation_name}</Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        {loadNote ? <Alert type="info" showIcon style={{ marginBottom: 16 }} message={loadNote} /> : null}
        {summary.overloadedCount > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${summary.overloadedCount} buckets are overloaded. Planned routing hours exceed available shift hours.`}
          />
        ) : null}
        {/* Summary Statistics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="Available Capacity"
                value={summary.totalAvailable}
                suffix="hrs"
                precision={1}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Planned Capacity"
                value={summary.totalPlanned}
                suffix="hrs"
                precision={1}
                valueStyle={{ color: '#145A54' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Actual Capacity"
                value={summary.totalActual}
                suffix="hrs"
                precision={1}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Overtime"
                value={summary.totalOvertime}
                suffix="hrs"
                precision={1}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Idle Time"
                value={summary.totalIdle}
                suffix="hrs"
                precision={1}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Avg Utilization"
                value={summary.avgUtilization}
                suffix="%"
                precision={0}
                valueStyle={{ color: summary.avgUtilization > 80 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Alerts for bottlenecks and overloads */}
        {(summary.bottleneckCount > 0 || summary.overloadedCount > 0) && (
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            {summary.bottleneckCount > 0 && (
              <Alert
                message="Bottleneck Alert"
                description={`${summary.bottleneckCount} workstation(s) identified as bottlenecks. Consider adding capacity or rescheduling.`}
                type="error"
                showIcon
                icon={<WarningOutlined />}
              />
            )}
            {summary.overloadedCount > 0 && (
              <Alert
                message="Overloaded Workstations"
                description={`${summary.overloadedCount} workstation(s) are overloaded (>100% utilization). Overtime or additional shifts may be required.`}
                type="warning"
                showIcon
                icon={<ThunderboltOutlined />}
              />
            )}
          </Space>
        )}

        <Table
          columns={columns}
          dataSource={capacityPlansData}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1600 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} capacity plans`,
          }}
          locale={{
            emptyText: 'No capacity plans found. Data will appear once capacity plans are generated.',
          }}
          rowClassName={(record: any) => {
            if (record.is_bottleneck) return 'row-bottleneck';
            if (record.is_overloaded) return 'row-overloaded';
            return '';
          }}
        />
      </Card>

      <style>{`
        .row-bottleneck {
          background-color: #fff1f0;
        }
        .row-overloaded {
          background-color: #fffbe6;
        }
      `}</style>
    </div>
  );
};

export default CapacityPlanningPage;

